import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Card,
  Row,
  Col,
  Typography,
  Tooltip,
  Badge,
  Drawer,
  DatePicker,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import moment from "moment";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const IssuesPage = () => {
  const { company, user } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [editingIssue, setEditingIssue] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueComments, setIssueComments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);

  const statusColors = {
    Open: "red",
    "In Progress": "orange",
    Resolved: "green",
    Closed: "gray",
    "On Hold": "purple",
  };

  const severityColors = {
    Critical: "red",
    High: "volcano",
    Medium: "orange",
    Low: "blue",
    Negligible: "cyan",
  };

  const riskImpactColors = {
    High: "red",
    Medium: "orange",
    Low: "green",
  };

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      // Get issues associated with this company
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/issues?fields=*.*&filter[company]=${company.id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      // Sort issues by created date (most recent first)
      const sortedIssues = response.data.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setIssues(sortedIssues);
    } catch (error) {
      console.error("Error fetching issues:", error);
      message.error("Failed to fetch issues");
    } finally {
      setLoading(false);
    }
  };

  const fetchIssueComments = async (issueId) => {
    setCommentLoading(true);
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/issue_comments?fields=*.*&filter[issue]=${issueId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      // Sort comments by created date
      const sortedComments = response.data.data.sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );

      setIssueComments(sortedComments);
    } catch (error) {
      console.error("Error fetching issue comments:", error);
    } finally {
      setCommentLoading(false);
    }
  };

  const showModal = (issue = null) => {
    setEditingIssue(issue);
    if (issue) {
      form.setFieldsValue({
        title: issue.title,
        description: issue.description,
        status: issue.status,
        severity: issue.severity,
        risk_impact: issue.risk_impact,
        due_date: issue.due_date ? moment(issue.due_date) : null,
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const showDrawer = (issue) => {
    setSelectedIssue(issue);
    fetchIssueComments(issue.id);
    setIsDrawerVisible(true);
  };

  const handleModalOk = () => {
    form.submit();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingIssue(null);
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
    setSelectedIssue(null);
    setIssueComments([]);
  };

  const handleFormSubmit = async (values) => {
    try {
      if (editingIssue) {
        // Update existing issue
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/loan_issues/${editingIssue.id}`,
          {
            ...values,
            due_date: values.due_date
              ? values.due_date.format("YYYY-MM-DD")
              : null,
            updated_by: user.id,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        message.success("Issue updated successfully");
      } else {
        // Create new issue
        await axios.post(
          "https://zedlenders.pickmesms.com/items/loan_issues",
          {
            ...values,
            due_date: values.due_date
              ? values.due_date.format("YYYY-MM-DD")
              : null,
            company: company.id,
            created_by: user.id,
            updated_by: user.id,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        message.success("Issue created successfully");
      }
      setIsModalVisible(false);
      setEditingIssue(null);
      fetchIssues();
    } catch (error) {
      console.error("Error saving issue:", error);
      message.error("Failed to save issue");
    }
  };

  const handleStatusChange = async (issueId, newStatus) => {
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loan_issues/${issueId}`,
        {
          status: newStatus,
          updated_by: user.id,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      message.success(`Status updated to ${newStatus}`);
      fetchIssues();
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Failed to update status");
    }
  };

  const handleSeverityChange = async (issueId, newSeverity) => {
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loan_issues/${issueId}`,
        {
          severity: newSeverity,
          updated_by: user.id,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      message.success(`Severity updated to ${newSeverity}`);
      fetchIssues();
    } catch (error) {
      console.error("Error updating severity:", error);
      message.error("Failed to update severity");
    }
  };

  const handleAddComment = async () => {
    try {
      const values = await commentForm.validateFields();
      await axios.post(
        "https://zedlenders.pickmesms.com/items/issue_comments",
        {
          issue: selectedIssue.id,
          comment: values.comment,
          created_by: user.id,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      message.success("Comment added successfully");
      commentForm.resetFields();
      fetchIssueComments(selectedIssue.id);
    } catch (error) {
      console.error("Error adding comment:", error);
      message.error("Failed to add comment");
    }
  };

  const handleDeleteIssue = async (issueId) => {
    try {
      await axios.delete(
        `https://zedlenders.pickmesms.com/items/issues/${issueId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      message.success("Issue deleted successfully");
      fetchIssues();
    } catch (error) {
      console.error("Error deleting issue:", error);
      message.error("Failed to delete issue");
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <a onClick={() => showDrawer(record)}>{text}</a>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Select
          value={status}
          style={{ width: 120 }}
          onChange={(value) => handleStatusChange(record.id, value)}
          dropdownMatchSelectWidth={false}
        >
          <Option value="Open">Open</Option>
          <Option value="In Progress">In Progress</Option>
          <Option value="Resolved">Resolved</Option>
          <Option value="Closed">Closed</Option>
          <Option value="On Hold">On Hold</Option>
        </Select>
      ),
      filters: [
        { text: "Open", value: "Open" },
        { text: "In Progress", value: "In Progress" },
        { text: "Resolved", value: "Resolved" },
        { text: "Closed", value: "Closed" },
        { text: "On Hold", value: "On Hold" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      render: (severity, record) => (
        <Select
          value={severity}
          style={{ width: 120 }}
          onChange={(value) => handleSeverityChange(record.id, value)}
          dropdownMatchSelectWidth={false}
        >
          <Option value="Critical">Critical</Option>
          <Option value="High">High</Option>
          <Option value="Medium">Medium</Option>
          <Option value="Low">Low</Option>
          <Option value="Negligible">Negligible</Option>
        </Select>
      ),
      filters: [
        { text: "Critical", value: "Critical" },
        { text: "High", value: "High" },
        { text: "Medium", value: "Medium" },
        { text: "Low", value: "Low" },
        { text: "Negligible", value: "Negligible" },
      ],
      onFilter: (value, record) => record.severity === value,
    },
    {
      title: "Risk Impact",
      dataIndex: "risk_impact",
      key: "risk_impact",
      render: (impact) => (
        <Tag color={riskImpactColors[impact] || "default"}>{impact}</Tag>
      ),
      filters: [
        { text: "High", value: "High" },
        { text: "Medium", value: "Medium" },
        { text: "Low", value: "Low" },
      ],
      onFilter: (value, record) => record.risk_impact === value,
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
      sorter: (a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => moment(date).format("YYYY-MM-DD"),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => showDrawer(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => showModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this issue?"
            onConfirm={() => handleDeleteIssue(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={3}>Issues & Risks</Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              New Issue
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={issues}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender: (record) => (
              <p style={{ margin: 0 }}>
                <strong>Description:</strong> {record.description}
              </p>
            ),
          }}
        />

        {/* Add/Edit Issue Modal */}
        <Modal
          title={editingIssue ? "Edit Issue" : "New Issue"}
          visible={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={600}
        >
          <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: "Please enter issue title" }]}
            >
              <Input placeholder="Enter issue title" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[
                { required: true, message: "Please enter issue description" },
              ]}
            >
              <TextArea rows={4} placeholder="Enter issue description" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="status"
                  label="Status"
                  initialValue="Open"
                  rules={[{ required: true, message: "Please select status" }]}
                >
                  <Select>
                    <Option value="Open">Open</Option>
                    <Option value="In Progress">In Progress</Option>
                    <Option value="Resolved">Resolved</Option>
                    <Option value="Closed">Closed</Option>
                    <Option value="On Hold">On Hold</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="severity"
                  label="Severity"
                  initialValue="Medium"
                  rules={[
                    { required: true, message: "Please select severity" },
                  ]}
                >
                  <Select>
                    <Option value="Critical">Critical</Option>
                    <Option value="High">High</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="Low">Low</Option>
                    <Option value="Negligible">Negligible</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="risk_impact"
                  label="Risk Impact"
                  initialValue="Medium"
                  rules={[
                    { required: true, message: "Please select risk impact" },
                  ]}
                >
                  <Select>
                    <Option value="High">High</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="Low">Low</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="due_date" label="Due Date">
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Issue Details Drawer */}
        <Drawer
          title={selectedIssue?.title || "Issue Details"}
          placement="right"
          width={600}
          onClose={handleDrawerClose}
          visible={isDrawerVisible}
          extra={
            selectedIssue && (
              <Space>
                <Button
                  onClick={() => showModal(selectedIssue)}
                  icon={<EditOutlined />}
                >
                  Edit
                </Button>
              </Space>
            )
          }
        >
          {selectedIssue && (
            <>
              <div style={{ marginBottom: 20 }}>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Text type="secondary">Status:</Text>
                    <div>
                      <Tag
                        color={statusColors[selectedIssue.status]}
                        style={{ fontSize: "14px" }}
                      >
                        {selectedIssue.status}
                      </Tag>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">Severity:</Text>
                    <div>
                      <Tag
                        color={severityColors[selectedIssue.severity]}
                        style={{ fontSize: "14px" }}
                      >
                        {selectedIssue.severity}
                      </Tag>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">Risk Impact:</Text>
                    <div>
                      <Tag
                        color={riskImpactColors[selectedIssue.risk_impact]}
                        style={{ fontSize: "14px" }}
                      >
                        {selectedIssue.risk_impact}
                      </Tag>
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={{ marginBottom: 20 }}>
                <Title level={5}>Description</Title>
                <p>{selectedIssue.description}</p>
              </div>

              <div style={{ marginBottom: 20 }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text type="secondary">Created:</Text>
                    <div>
                      {moment(selectedIssue.created_at).format(
                        "YYYY-MM-DD HH:mm"
                      )}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">Due Date:</Text>
                    <div>
                      {selectedIssue.due_date
                        ? moment(selectedIssue.due_date).format("YYYY-MM-DD")
                        : "N/A"}
                    </div>
                  </Col>
                </Row>
              </div>

              <div>
                <Title level={5}>
                  Comments
                  <Badge
                    count={issueComments.length}
                    style={{ backgroundColor: "#52c41a", marginLeft: 8 }}
                  />
                </Title>

                {commentLoading ? (
                  <div style={{ textAlign: "center", margin: "20px 0" }}>
                    Loading comments...
                  </div>
                ) : (
                  <>
                    {issueComments.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          margin: "20px 0",
                          color: "#999",
                        }}
                      >
                        No comments yet
                      </div>
                    ) : (
                      <div
                        style={{
                          maxHeight: "300px",
                          overflow: "auto",
                          marginBottom: 16,
                        }}
                      >
                        {issueComments.map((comment) => (
                          <Card
                            key={comment.id}
                            size="small"
                            style={{ marginBottom: 8 }}
                          >
                            <div>
                              <div style={{ marginBottom: 4 }}>
                                <Text strong>
                                  {comment.created_by?.first_name}{" "}
                                  {comment.created_by?.last_name}
                                </Text>
                                <Text
                                  type="secondary"
                                  style={{ marginLeft: 8, fontSize: 12 }}
                                >
                                  {moment(comment.created_at).format(
                                    "YYYY-MM-DD HH:mm"
                                  )}
                                </Text>
                              </div>
                              <div>{comment.comment}</div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}

                    <Form
                      form={commentForm}
                      layout="inline"
                      style={{ display: "flex", marginTop: 16 }}
                    >
                      <Form.Item
                        name="comment"
                        rules={[
                          {
                            required: true,
                            message: "Comment cannot be empty",
                          },
                        ]}
                        style={{ flex: 1, marginRight: 8 }}
                      >
                        <Input placeholder="Add a comment..." />
                      </Form.Item>
                      <Form.Item>
                        <Button
                          type="primary"
                          icon={<CommentOutlined />}
                          onClick={handleAddComment}
                        >
                          Add
                        </Button>
                      </Form.Item>
                    </Form>
                  </>
                )}
              </div>
            </>
          )}
        </Drawer>
      </Card>
    </div>
  );
};

export default IssuesPage;
